import { Auth } from 'aws-amplify';
import AWS from 'aws-sdk';
import moment from 'moment';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cognitoConfig } from '../../config/cognito';
import { environment } from '../../config/env';
import PageLoader from '../components/PageLoader';
import { AdminRoles } from '../../config/roles';
import PermissionDenied from '../components/PermissionDenied';

const cognitoidentity = new AWS.CognitoIdentity({
  apiVersion: '2014-06-30',
  region: cognitoConfig.userPool.region,
});

export const AUTH_STATUS = {
  NOT_INITIATED: 'not-initiated',
  LOADING: 'loading',
  DONE: 'done',
  ERROR: 'error',
};

export const AUTH = {
  mandatorySignIn: false,
  region: cognitoConfig.userPool.region,
  userPoolId: cognitoConfig.userPool.UserPoolId,
  identityPoolId: cognitoConfig.identityPoolId,
  userPoolWebClientId: cognitoConfig.userPool.ClientId,
  oauth: {
    domain: cognitoConfig.oAuthDomain,
    redirectSignIn: cognitoConfig.oAuthRedirectSignIn,
    redirectSignOut: cognitoConfig.oAuthRedirectSignOut,
    responseType: cognitoConfig.oAuthRespType,
    client_id: cognitoConfig.userPool.ClientId,
  },
  federationTarget: 'COGNITO_USER_POOLS',
};

const AuthContext = createContext();
export const useAuthContext = () => useContext(AuthContext);

export const AuthGuard = ({ children }) => {
  const { user, authStatus } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (
      !user &&
      ![AUTH_STATUS.NOT_INITIATED, AUTH_STATUS.LOADING].includes(authStatus) &&
      location
    ) {
      console.log('redirection to login');
      navigate(`/login?redirect_uri=${btoa(location.pathname)}`);
    }
  }, [user, authStatus, location]);

  if (user) return children;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        height: '100%',
        overflow: 'scroll',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <PageLoader text="Please wait while we load your application" />
    </div>
  );
};

const AuthContextProvider = ({ children }) => {
  const [user, setUser] = useState();
  const [permissionError, setPermissionError] = useState(false);
  const [fetchRetryCount, setFetchRetryCount] = useState(0);
  const [authStatus, setAuthStatus] = useState(AUTH_STATUS.NOT_INITIATED);

  const signIn = async () => {
    setAuthStatus(AUTH_STATUS.LOADING);
    try {
      const user = await Auth.currentAuthenticatedUser();
      if (user) {
        const userSession = user.signInUserSession;
        setUser({
          userId: userSession.idToken.payload.sub,
          username: user.attributes.email,
          autodesk_access_token: userSession.idToken.jwtToken,
          autodesk_exp: userSession.idToken.payload.exp,
          idToken: userSession.idToken.jwtToken,
          authenticated: true,
        });
        setAuthStatus(AUTH_STATUS.DONE);
      } else {
        throw Error('null user');
      }
    } catch (error) {
      setAuthStatus(AUTH_STATUS.LOADING);
      window.location.assign(cognitoConfig.ssoUrl);
    }
  };

  const fetchUser = useCallback(async () => {
    setAuthStatus(AUTH_STATUS.LOADING);
    setFetchRetryCount((c) => (c += 1));
    try {
      const user = await Auth.currentAuthenticatedUser();
      if (user) {
        const userSession = user.signInUserSession;

        if (
          userSession.idToken.payload['cognito:groups'] &&
          userSession.idToken.payload['cognito:groups'].filter((value) =>
            AdminRoles.includes(value)
          ).length > 0 // modify this once the development is done
        ) {
          await updateConfigCredentials(
            environment.AgentAssistUserRoleArn,
            userSession.idToken.jwtToken
          );
          setUser({
            userId: userSession.idToken.payload.sub,
            username: user.attributes.email,
            autodesk_access_token: userSession.idToken.jwtToken,
            autodesk_exp: userSession.idToken.payload.exp,
            idToken: userSession.idToken.jwtToken,
            authenticated: true,
          });
          setAuthStatus(AUTH_STATUS.DONE);
        } else {
          setPermissionError(true);
        }
      }
    } catch (error) {
      setAuthStatus(AUTH_STATUS.ERROR);
      console.error(error);
    }
  }, [fetchRetryCount]);

  useEffect(() => {
    if (!user) fetchUser();
  }, []);

  if (permissionError) {
    return <PermissionDenied />;
  }

  return (
    <AuthContext.Provider value={{ user, setUser, signIn, authStatus }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;

async function updateConfigCredentials(customRoleArn, userToken) {
  if (AWS.config.credentials?.expireTime > moment().unix()) return;
  const userPoolLoginKey = `cognito-idp.${cognitoConfig.userPool.region}.amazonaws.com/${cognitoConfig.userPool.UserPoolId}`;
  const credentials = await Auth.currentUserCredentials();
  const params = {
    IdentityId: credentials.identityId,
    Logins: {
      [userPoolLoginKey]: userToken,
    },
    CustomRoleArn: customRoleArn,
  };
  const data = await cognitoidentity.getCredentialsForIdentity(params).promise();
  const cred = {
    accessKeyId: data.Credentials.AccessKeyId,
    sessionToken: data.Credentials.SessionToken,
    secretAccessKey: data.Credentials.SecretKey,
    expireTime: data.Credentials.Expiration,
    expired: false,
  };
  AWS.config.update({
    region: cognitoConfig.userPool.region,
    credentials: cred,
  });
}
